import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY, formatApiError } from "@/api/client";

const AuthContext = createContext(null);

/**
 * Auth state machine (post Phase 10.2):
 *
 *   Cookie flow (new default)
 *     - Login  → backend sets `access_token` (httpOnly) + `csrf_token`
 *       cookies. Response body: `{token: "", csrf_token, user}`. We store
 *       only the user object in React state; the browser handles the
 *       cookie for us on every request.
 *     - Refresh → we call `/auth/me` unconditionally. If the cookie is
 *       there, we get 200 + user; if not, 401 → anonymous.
 *
 *   Legacy Bearer flow (transition compat)
 *     - Users who logged in before this deploy still hold a JWT in
 *       `localStorage[TOKEN_KEY]`. The axios interceptor sends it as
 *       `Authorization: Bearer …` and the backend still accepts it.
 *     - As soon as they log out (or the token expires), the localStorage
 *       key is wiped and the next login goes cookie-only.
 */
export function AuthProvider({ children }) {
  // user: null = checking, false = anonymous, object = authenticated
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      // Always attempt /me: the axios instance sends both the cookie
      // (if any) and the Bearer header (if any). One of them is enough.
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 = not logged in; clear the legacy token so the axios
      // interceptor stops sending a stale Bearer.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_KEY);
      } else {
        // Network / server errors: don't wipe local state, we'll retry.
        console.debug("AuthContext.refresh: transient error:", status || err?.message);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });

    // If the backend returned a JWT in body (mobile / CLI path), stash
    // it. In the SPA path the field is "" and we rely purely on the
    // httpOnly cookie the browser just accepted.
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
    } else {
      // New cookie flow — proactively wipe any stale legacy token so
      // subsequent requests use ONLY the cookie (cleaner audit trail).
      localStorage.removeItem(TOKEN_KEY);
    }

    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side session may already be expired (401). We still
      // clear local state below so the user is logged out regardless.
      console.debug("AuthContext.logout: server-side cleanup skipped:", err?.response?.status);
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }, []);

  const hasPermission = useCallback(
    (perm) => {
      if (!user || user === false) return false;
      if (user.role === "admin") return true;
      const perms = user.permissions || [];
      return perms.includes("*") || perms.includes(perm);
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{ user, login, logout, refresh, hasPermission, formatApiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
